import {
  AbsenceKindEnum,
  OvertimeLegalReasonEnum,
  PortalDayFragment,
  PunchKindEnum,
  ScheduleTypeEnum,
  TimeClassificationKindEnum,
  usePortalAbsenceRequestMutation,
  usePortalEmployeeProfileSaveMutation,
  usePortalEmployeeQuery,
  usePortalMonthApproveMutation,
  usePortalOvertimeRequestCreateMutation,
  usePortalPunchCorrectMutation,
  usePortalTimeClassifyMutation,
} from "@dashboard/graphql";
import { Box, Button, Checkbox, Input, Option, Select, Text } from "@saleor/macaw-ui-next";
import { Fragment, useEffect, useMemo, useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";
import { Link, RouteComponentProps } from "react-router-dom";

import { AbsenceList, useAbsenceKindLabel } from "../AbsenceList";
import {
  DayCard,
  ErrorText,
  KIND_MESSAGES,
  Page,
  Section,
  useClassificationLabel,
} from "../components";
import { DispensationPeriods, PARTIAL_KINDS, Period } from "../DispensationPeriods";
import { ExportButton } from "../ExportButton";
import { portalLabels as l } from "../labels";
import { portalMessages as m } from "../messages";
import { LegalReasonSelect, OvertimeCountersView } from "../OvertimeParts";
import { portalPaths } from "../urls";
import {
  addDays,
  formatDateTime,
  formatDay,
  formatMinutes,
  isoDay,
  today,
  weekdayShort,
  weekStart,
} from "../utils";
import { VacationPreview } from "../VacationPreview";
import { Week, weekFromRows, WeekScheduleEditor, weekToRows } from "../WeekScheduleEditor";
import { VacationBalance } from "./Absences";

type Form = Record<string, string | boolean>;

const TEXT_FIELDS: Array<[string, MessageDescriptor]> = [
  ["fullName", l.fullName],
  ["birthDate", l.birthDate],
  ["nif", l.nif],
  ["niss", l.niss],
  ["phone", l.phone],
  ["personalEmail", l.personalEmailHint],
  ["streetAddress", l.address],
  ["postalCode", l.postalCode],
  ["city", l.city],
  ["emergencyContactName", l.emergencyContactName],
  ["emergencyContactPhone", l.emergencyContactPhone],
  ["jobTitle", l.jobTitle],
  ["hireDate", l.hireDate],
  ["terminationDate", l.terminationDate],
  ["weeklyHours", l.weeklyHours],
  ["dailyHours", l.dailyHours],
  ["annualVacationDays", l.annualVacationDays],
  ["carriedVacationDays", m.carriedVacationDays],
];

const DATE_FIELDS = new Set(["birthDate", "hireDate", "terminationDate"]);
const NUMBER_FIELDS = new Set([
  "weeklyHours",
  "dailyHours",
  "annualVacationDays",
  "carriedVacationDays",
]);

const toLocalInput = (iso: string) => {
  const date = new Date(iso);
  const day = isoDay(date);
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Lisbon",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);

  return `${day}T${time}`;
};

/** "2026-10-13T09:30" in Lisbon → ISO with the right offset. */
const fromLocalInput = (value: string) => {
  const guess = new Date(`${value}:00Z`);
  const lisbon = new Date(guess.toLocaleString("en-US", { timeZone: "Europe/Lisbon" }));
  const utc = new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));

  return new Date(guess.getTime() - (lisbon.getTime() - utc.getTime())).toISOString();
};

const CorrectionForm = ({
  userId,
  punchId,
  initial,
  onDone,
}: {
  userId: string;
  punchId?: string;
  initial?: { kind: PunchKindEnum; effectiveAt: string };
  onDone: () => void;
}) => {
  const intl = useIntl();
  const [kind, setKind] = useState<PunchKindEnum>(initial?.kind ?? PunchKindEnum.IN);
  const [when, setWhen] = useState(
    initial ? toLocalInput(initial.effectiveAt) : `${today()}T09:00`,
  );
  const [reason, setReason] = useState("");
  const [correct, opts] = usePortalPunchCorrectMutation({
    onCompleted: data => {
      if (!data.punchCorrect?.errors.length) {
        onDone();
      }
    },
  });
  const kindOptions: Option[] = Object.values(PunchKindEnum).map(value => ({
    value,
    label: intl.formatMessage(KIND_MESSAGES[value]),
  }));

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      padding={2}
      backgroundColor="default2"
      borderRadius={3}
    >
      <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))">
        <Select
          size="small"
          label={intl.formatMessage(m.kind)}
          options={kindOptions}
          value={kindOptions.find(o => o.value === kind) ?? null}
          onChange={option => setKind(option.value as PunchKindEnum)}
        />
        <Input
          size="small"
          type="datetime-local"
          label={intl.formatMessage(m.time)}
          value={when}
          onChange={event => setWhen(event.target.value)}
        />
        <Input
          size="small"
          label={intl.formatMessage(m.reason)}
          value={reason}
          maxLength={2000}
          onChange={event => setReason(event.target.value)}
        />
      </Box>
      <Box display="flex" gap={2}>
        <Button
          size="small"
          variant="primary"
          disabled={!reason.trim() || opts.loading}
          onClick={() =>
            correct({
              variables: {
                id: punchId,
                userId: punchId ? undefined : userId,
                kind,
                effectiveAt: fromLocalInput(when),
                reason: reason.trim(),
              },
            })
          }
        >
          {intl.formatMessage(m.save)}
        </Button>
        {punchId && (
          <Button
            size="small"
            variant="error"
            disabled={!reason.trim() || opts.loading}
            onClick={() =>
              correct({ variables: { id: punchId, voids: true, reason: reason.trim() } })
            }
          >
            {intl.formatMessage(m.voidPunch)}
          </Button>
        )}
        <Button size="small" variant="secondary" onClick={onDone}>
          {intl.formatMessage(m.cancel)}
        </Button>
      </Box>
      <ErrorText errors={opts.data?.punchCorrect?.errors} />
    </Box>
  );
};

const ClassifyForm = ({
  userId,
  day,
  onDone,
}: {
  userId: string;
  day: PortalDayFragment;
  onDone: () => void;
}) => {
  const intl = useIntl();
  const classificationLabel = useClassificationLabel();
  const [kind, setKind] = useState<TimeClassificationKindEnum>(TimeClassificationKindEnum.OVERTIME);
  const [minutes, setMinutes] = useState(String(day.offScheduleMinutes || 0));
  const [reason, setReason] = useState("");
  const [classify, opts] = usePortalTimeClassifyMutation({
    onCompleted: data => {
      if (!data.timeClassify?.errors.length) {
        onDone();
      }
    },
  });
  const options: Option[] = Object.values(TimeClassificationKindEnum).map(value => ({
    value,
    label: classificationLabel(value),
  }));

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      padding={2}
      backgroundColor="default2"
      borderRadius={3}
    >
      <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))">
        <Select
          size="small"
          label={intl.formatMessage(m.kind)}
          options={options}
          value={options.find(o => o.value === kind) ?? null}
          onChange={option => setKind(option.value as TimeClassificationKindEnum)}
        />
        <Input
          size="small"
          type="number"
          min={0}
          label={intl.formatMessage(m.minutes)}
          value={minutes}
          onChange={event => setMinutes(event.target.value)}
        />
        <Input
          size="small"
          label={intl.formatMessage(m.reason)}
          value={reason}
          maxLength={2000}
          onChange={event => setReason(event.target.value)}
        />
      </Box>
      <Text size={1} color="default2">
        {intl.formatMessage(m.realWorkHint)}
      </Text>
      <Box display="flex" gap={2}>
        <Button
          size="small"
          variant="primary"
          disabled={!reason.trim() || opts.loading}
          onClick={() =>
            classify({
              variables: {
                userId,
                date: day.date,
                kind,
                minutes: Number(minutes) || 0,
                reason: reason.trim(),
              },
            })
          }
        >
          {intl.formatMessage(m.save)}
        </Button>
        <Button size="small" variant="secondary" onClick={onDone}>
          {intl.formatMessage(m.cancel)}
        </Button>
      </Box>
      <ErrorText errors={opts.data?.timeClassify?.errors} />
    </Box>
  );
};

const OrderOvertime = ({ userId, onDone }: { userId: string; onDone: () => void }) => {
  const intl = useIntl();
  const [form, setForm] = useState({ date: today(), minutes: "60", reason: "" });
  const [legalReason, setLegalReason] = useState<OvertimeLegalReasonEnum | "">("");
  const [order, opts] = usePortalOvertimeRequestCreateMutation({
    onCompleted: result => {
      if (!result.overtimeRequestCreate?.errors.length) {
        setForm({ date: today(), minutes: "60", reason: "" });
        onDone();
      }
    },
  });

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Text size={3} fontWeight="bold">
        {intl.formatMessage(m.orderOvertime)}
      </Text>
      <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))">
        <Input
          size="small"
          type="date"
          label={intl.formatMessage(m.date)}
          value={form.date}
          onChange={e => setForm({ ...form, date: e.target.value })}
        />
        <Input
          size="small"
          type="number"
          min={15}
          step={15}
          label={intl.formatMessage(m.minutes)}
          value={form.minutes}
          onChange={e => setForm({ ...form, minutes: e.target.value })}
        />
        <Input
          size="small"
          label={intl.formatMessage(m.reason)}
          value={form.reason}
          maxLength={2000}
          onChange={e => setForm({ ...form, reason: e.target.value })}
        />
        <LegalReasonSelect value={legalReason} onChange={setLegalReason} />
      </Box>
      <ErrorText errors={opts.data?.overtimeRequestCreate?.errors} />
      <Box>
        <Button
          size="small"
          variant="primary"
          disabled={!form.reason.trim() || !legalReason || !Number(form.minutes) || opts.loading}
          onClick={() =>
            order({
              variables: {
                userId,
                date: form.date,
                minutes: Number(form.minutes),
                reason: form.reason.trim(),
                legalReason: legalReason || undefined,
              },
            })
          }
        >
          {intl.formatMessage(m.orderOvertime)}
        </Button>
      </Box>
    </Box>
  );
};

export const Employee = ({ match }: RouteComponentProps<{ userId: string }>) => {
  const intl = useIntl();
  const absenceKindLabel = useAbsenceKindLabel();
  const userId = decodeURIComponent(match.params.userId);
  const [start, setStart] = useState(() => weekStart(today()));
  const end = addDays(start, 6);
  const { data, refetch } = usePortalEmployeeQuery({
    variables: { userId, start, end },
    fetchPolicy: "cache-and-network",
  });
  const profile = data?.employeeProfile;
  const [form, setForm] = useState<Form>({});
  const [schedule, setSchedule] = useState<Week>({});
  // No schedule of their own: they follow the company's normal hours.
  const [followsDefault, setFollowsDefault] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [profileEditing, setProfileEditing] = useState(false);
  const [save, saveOpts] = usePortalEmployeeProfileSaveMutation({
    onCompleted: result => {
      if (!result.employeeProfileSave?.errors.length) {
        setProfileEditing(false);
        refetch();
      }
    },
  });
  const [absenceForm, setAbsenceForm] = useState({
    kind: AbsenceKindEnum.SICK_LEAVE as string,
    startDate: "",
    endDate: "",
    reason: "",
  });
  const [periods, setPeriods] = useState<Period[]>([{ start: "16:00", end: "18:00" }]);
  const [registerAbsence, absenceOpts] = usePortalAbsenceRequestMutation({
    onCompleted: result => {
      if (!result.absenceRequest?.errors.length) {
        setAbsenceForm({ ...absenceForm, startDate: "", endDate: "", reason: "" });
        refetch();
      }
    },
  });
  const lastMonth = (() => {
    const first = new Date(`${today().slice(0, 7)}-01T12:00:00Z`);

    first.setUTCMonth(first.getUTCMonth() - 1);

    return first.toISOString().slice(0, 10);
  })();
  const [approveMonth, approveOpts] = usePortalMonthApproveMutation({
    onCompleted: () => refetch(),
  });

  const resetForm = () => {
    if (!profile) {
      return;
    }

    const next: Form = {};

    TEXT_FIELDS.forEach(([field]) => {
      const value = (profile as Record<string, unknown>)[field];

      next[field] = value === null || value === undefined ? "" : String(value);
    });
    next.scheduleType = profile.scheduleType;
    next.overtimeExemption = profile.overtimeExemption;
    next.overtimeExemptionUntil = profile.overtimeExemptionUntil ?? "";
    next.teleworkAllowed = profile.teleworkAllowed;
    next.workSiteId = profile.workSite?.id ?? "";
    next.managerId = profile.managerId ?? "";
    setForm(next);

    const rows: Record<number, { start: string; end: string }[]> = {};

    profile.weeklySchedule.forEach(row => {
      rows[row.weekday] = [...(rows[row.weekday] ?? []), { start: row.start, end: row.end }];
    });
    setSchedule(rows);
    setFollowsDefault(profile.usesDefaultSchedule);
  };

  useEffect(resetForm, [profile]);

  useEffect(() => {
    const startDate = data?.timekeepingStartDate;

    if (startDate && end < startDate) {
      setStart(weekStart(startDate));
    }
  }, [data?.timekeepingStartDate, end]);

  const days: PortalDayFragment[] = useMemo(
    () =>
      (data?.employeePeriod?.days ?? []).map(day => ({
        ...day,
        punches: (data?.employeePunches ?? []).filter(
          punch => isoDay(new Date(punch.effectiveAt)) === day.date,
        ),
      })),
    [data],
  );

  const handleSave = () => {
    const input: Record<string, unknown> = {};

    TEXT_FIELDS.forEach(([field]) => {
      const value = String(form[field] ?? "");

      if (NUMBER_FIELDS.has(field)) {
        if (value) {
          input[field] = Number(value);
        }
      } else if (DATE_FIELDS.has(field)) {
        input[field] = value || null;
      } else {
        input[field] = value;
      }
    });
    input.scheduleType = form.scheduleType;
    input.overtimeExemption = form.overtimeExemption ?? "";
    input.overtimeExemptionUntil = form.overtimeExemptionUntil || null;
    input.teleworkAllowed = form.teleworkAllowed === true;
    input.workSiteId = form.workSiteId || null;
    input.managerId = form.managerId || null;
    input.weeklySchedule = followsDefault ? [] : weekToRows(schedule);
    save({ variables: { userId, input } });
  };

  const siteOptions: Option[] = [
    { value: "", label: "—" },
    ...(data?.workSites ?? []).map(site => ({ value: site.id, label: site.name })),
  ];
  const managerOptions: Option[] = [
    { value: "", label: intl.formatMessage(l.allManagement) },
    ...(data?.employeeProfiles ?? [])
      .filter(person => person.userId !== userId)
      .map(person => ({ value: person.userId, label: person.fullName })),
  ];
  const exemptionOptions: Option[] = [
    { value: "", label: "—" },
    { value: "pregnant", label: intl.formatMessage(m.exemptPregnant) },
    { value: "child_under_12m", label: intl.formatMessage(m.exemptChild) },
    { value: "breastfeeding", label: intl.formatMessage(m.exemptBreastfeeding) },
    { value: "disability", label: intl.formatMessage(m.exemptDisability) },
    { value: "student", label: intl.formatMessage(m.exemptStudent) },
    { value: "minor", label: intl.formatMessage(m.exemptMinor) },
  ];
  const scheduleOptions: Option[] = [
    { value: ScheduleTypeEnum.FIXED, label: intl.formatMessage(l.scheduleFixed) },
    { value: ScheduleTypeEnum.SHIFTS, label: intl.formatMessage(l.scheduleShifts) },
    { value: ScheduleTypeEnum.EXEMPT, label: intl.formatMessage(l.scheduleExempt) },
  ];

  if (!profile) {
    return null;
  }

  const period = data?.employeePeriod;

  return (
    <Page>
      <Box display="flex" alignItems="center" gap={3}>
        <Link to={portalPaths.team}>
          <Button size="small" variant="tertiary" as="span">
            ‹ {intl.formatMessage(m.back)}
          </Button>
        </Link>
        <Text size={7} fontWeight="bold">
          {profile.fullName}
        </Text>
        <ExportButton userId={userId} />
      </Box>

      <Section
        title={intl.formatMessage(m.week, { date: `${formatDay(start)} – ${formatDay(end)}` })}
        actions={
          <Box display="flex" gap={2}>
            <Button
              size="small"
              variant="secondary"
              disabled={
                !!data?.timekeepingStartDate && start <= weekStart(data.timekeepingStartDate)
              }
              onClick={() => setStart(addDays(start, -7))}
            >
              ‹
            </Button>
            <Button size="small" variant="secondary" onClick={() => setStart(addDays(start, 7))}>
              ›
            </Button>
            <Button size="small" variant="secondary" onClick={() => setEditing("new")}>
              {intl.formatMessage(m.addPunch)}
            </Button>
          </Box>
        }
      >
        {period && (
          <Text size={3}>
            {intl.formatMessage(m.registered)}: <b>{formatMinutes(period.workedMinutes)}</b> ·{" "}
            {intl.formatMessage(m.contracted)}: {formatMinutes(period.contractedMinutes)} ·{" "}
            {intl.formatMessage(m.balance)}: <b>{formatMinutes(period.balanceMinutes, true)}</b>
            {period.signature &&
              ` · ${intl.formatMessage(period.signature.agreed ? m.agreed : m.disagreed)}${
                period.signature.note ? `: “${period.signature.note}”` : ""
              }`}
          </Text>
        )}
        {editing === "new" && (
          <CorrectionForm
            userId={userId}
            onDone={() => {
              setEditing(null);
              refetch();
            }}
          />
        )}
        {days.map(day => (
          <DayCard
            key={day.date}
            day={day}
            punchActions={punch =>
              punch.isCorrection ? null : (
                <Button size="small" variant="tertiary" onClick={() => setEditing(punch.id)}>
                  {intl.formatMessage(m.correct)}
                </Button>
              )
            }
            footer={
              <>
                {day.punches
                  .filter(punch => punch.id === editing)
                  .map(punch => (
                    <CorrectionForm
                      key={punch.id}
                      userId={userId}
                      punchId={punch.id}
                      initial={{ kind: punch.kind, effectiveAt: punch.effectiveAt }}
                      onDone={() => {
                        setEditing(null);
                        refetch();
                      }}
                    />
                  ))}
                {editing === `classify-${day.date}` ? (
                  <ClassifyForm
                    userId={userId}
                    day={day}
                    onDone={() => {
                      setEditing(null);
                      refetch();
                    }}
                  />
                ) : (
                  (day.offScheduleMinutes > 0 || day.workedMinutes !== day.plannedMinutes) &&
                  day.date < today() && (
                    <Box>
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => setEditing(`classify-${day.date}`)}
                      >
                        {intl.formatMessage(m.classify)}
                      </Button>
                    </Box>
                  )
                )}
              </>
            }
          />
        ))}
      </Section>

      {profile.scheduleChanges.length > 0 && (
        <Section title={intl.formatMessage(m.scheduleHistory)}>
          {profile.scheduleChanges.map(change => (
            <Text key={change.id} size={2}>
              {formatDateTime(change.changedAt)} · {change.changedByName} ·{" "}
              {change.weeklySchedule
                .map(row => `${weekdayShort(intl.locale, row.weekday)} ${row.start}–${row.end}`)
                .join(", ") || "—"}
            </Text>
          ))}
          <Text size={1} color="default2">
            {intl.formatMessage(m.scheduleHistoryHint)}
          </Text>
        </Section>
      )}

      <Section title={intl.formatMessage(m.overtimeSection)}>
        {data?.overtimeCounters && <OvertimeCountersView counters={data.overtimeCounters} />}
        <OrderOvertime userId={userId} onDone={refetch} />
      </Section>

      <Section title={intl.formatMessage(m.absencesModule)}>
        <VacationBalance buckets={data?.employeeVacationBalance ?? []} detailed />
        <AbsenceList absences={data?.absences ?? []} manager onChanged={refetch} />
        <Text size={3} fontWeight="bold">
          {intl.formatMessage(m.registerAbsence)}
        </Text>
        <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(170px, 1fr))">
          <Select
            size="small"
            label={intl.formatMessage(m.kind)}
            options={Object.values(AbsenceKindEnum).map(kind => ({
              value: kind,
              label: absenceKindLabel(kind),
            }))}
            value={{ value: absenceForm.kind, label: absenceKindLabel(absenceForm.kind) }}
            onChange={option => setAbsenceForm({ ...absenceForm, kind: option.value })}
          />
          <Input
            size="small"
            type="date"
            label={intl.formatMessage(m.start)}
            value={absenceForm.startDate}
            onChange={e =>
              setAbsenceForm({
                ...absenceForm,
                startDate: e.target.value,
                endDate: absenceForm.endDate || e.target.value,
              })
            }
          />
          <Input
            size="small"
            type="date"
            label={intl.formatMessage(m.end)}
            value={absenceForm.endDate}
            onChange={e => setAbsenceForm({ ...absenceForm, endDate: e.target.value })}
          />
          <Input
            size="small"
            label={intl.formatMessage(m.reason)}
            value={absenceForm.reason}
            maxLength={2000}
            onChange={e => setAbsenceForm({ ...absenceForm, reason: e.target.value })}
          />
        </Box>
        {PARTIAL_KINDS.includes(absenceForm.kind) ? (
          <DispensationPeriods periods={periods} onChange={setPeriods} showHint />
        ) : (
          <VacationPreview
            start={absenceForm.startDate}
            end={absenceForm.endDate}
            vacation={absenceForm.kind === AbsenceKindEnum.VACATION}
            userId={userId}
          />
        )}
        {absenceForm.kind === AbsenceKindEnum.VACATION && (
          <Text size={1} color="default2">
            {intl.formatMessage(m.registerVacationHint)}
          </Text>
        )}
        <Box>
          <Button
            size="small"
            variant="primary"
            disabled={!absenceForm.startDate || !absenceForm.endDate || absenceOpts.loading}
            onClick={() =>
              registerAbsence({
                variables: {
                  ...absenceForm,
                  kind: absenceForm.kind as AbsenceKindEnum,
                  userId,
                  dailyPeriods: PARTIAL_KINDS.includes(absenceForm.kind) ? periods : undefined,
                },
              })
            }
          >
            {intl.formatMessage(m.save)}
          </Button>
        </Box>
        <ErrorText errors={absenceOpts.data?.absenceRequest?.errors} />
      </Section>

      <Section title={intl.formatMessage(m.monthApprovals)}>
        {(data?.monthApprovals ?? []).map(approval => (
          <Text key={approval.id} size={2}>
            {approval.month.slice(0, 7)} · {approval.approvedByName} ·{" "}
            {approval.summaryHash.slice(0, 16)}
          </Text>
        ))}
        {!data?.monthApprovals.some(a => a.month === lastMonth) && (
          <Box>
            <Button
              size="small"
              variant="primary"
              disabled={approveOpts.loading}
              onClick={() => approveMonth({ variables: { userId, month: lastMonth } })}
            >
              {intl.formatMessage(m.approveMonth, { month: lastMonth.slice(0, 7) })}
            </Button>
          </Box>
        )}
        <Text size={1} color="default2">
          {intl.formatMessage(m.approveMonthHint)}
        </Text>
        <ErrorText errors={approveOpts.data?.monthApprove?.errors} />
      </Section>

      <Section
        title={intl.formatMessage(m.profile)}
        actions={
          !profileEditing && (
            <Button size="small" variant="secondary" onClick={() => setProfileEditing(true)}>
              {intl.formatMessage(m.edit)}
            </Button>
          )
        }
      >
        <Box display="grid" gap={2} __gridTemplateColumns="minmax(160px, 260px) 1fr">
          <Text size={2} color="default2">
            {intl.formatMessage(m.function)}
          </Text>
          <Text size={3}>
            {profile.roleNames.join(", ") || "—"}{" "}
            <Link to={portalPaths.users}>
              <Text size={1} color="default2">
                ({intl.formatMessage(m.usersModule)})
              </Text>
            </Link>
          </Text>
        </Box>
        {!profileEditing ? (
          <Box display="grid" gap={2} __gridTemplateColumns="minmax(160px, 260px) 1fr">
            {[
              ...TEXT_FIELDS.map(
                ([field, labelMessage]) =>
                  [intl.formatMessage(labelMessage), String(form[field] ?? "")] as [string, string],
              ),
              [
                intl.formatMessage(l.scheduleType),
                scheduleOptions.find(o => o.value === form.scheduleType)?.label ?? "",
              ],
              [
                intl.formatMessage(l.schedule) +
                  (followsDefault ? ` (${intl.formatMessage(m.companyHours)})` : ""),
                Object.entries(schedule)
                  .flatMap(([weekday, rows]) =>
                    rows.map(
                      row =>
                        `${weekdayShort(intl.locale, Number(weekday))} ${row.start}–${row.end}`,
                    ),
                  )
                  .join(", "),
              ],
              [
                intl.formatMessage(l.workSite),
                siteOptions.find(o => o.value === form.workSiteId)?.label ?? "",
              ],
              [
                intl.formatMessage(l.directManager),
                managerOptions.find(o => o.value === form.managerId)?.label ?? "",
              ],
              [
                intl.formatMessage(l.telework),
                intl.formatMessage(form.teleworkAllowed ? m.yes : m.no),
              ],
              [
                intl.formatMessage(m.overtimeExemption),
                form.overtimeExemption
                  ? `${exemptionOptions.find(o => o.value === form.overtimeExemption)?.label ?? ""}${
                      form.overtimeExemptionUntil ? ` (${form.overtimeExemptionUntil})` : ""
                    }`
                  : "",
              ],
            ].map(([label, value]) => (
              <Fragment key={label}>
                <Text size={2} color="default2">
                  {label}
                </Text>
                <Text size={3}>{value || "—"}</Text>
              </Fragment>
            ))}
          </Box>
        ) : (
          <>
            <Box
              display="grid"
              gap={2}
              __gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))"
            >
              {TEXT_FIELDS.map(([field, labelMessage]) => (
                <Input
                  key={field}
                  size="small"
                  label={intl.formatMessage(labelMessage)}
                  type={
                    DATE_FIELDS.has(field) ? "date" : NUMBER_FIELDS.has(field) ? "number" : "text"
                  }
                  value={String(form[field] ?? "")}
                  maxLength={256}
                  onChange={event => setForm({ ...form, [field]: event.target.value })}
                />
              ))}
              <Select
                size="small"
                label={intl.formatMessage(l.scheduleType)}
                options={scheduleOptions}
                value={scheduleOptions.find(o => o.value === form.scheduleType) ?? null}
                onChange={option => setForm({ ...form, scheduleType: option.value })}
              />
              <Select
                size="small"
                label={intl.formatMessage(l.workSite)}
                options={siteOptions}
                value={siteOptions.find(o => o.value === form.workSiteId) ?? null}
                onChange={option => setForm({ ...form, workSiteId: option.value })}
              />
              <Select
                size="small"
                label={intl.formatMessage(l.directManager)}
                options={managerOptions}
                value={managerOptions.find(o => o.value === form.managerId) ?? null}
                onChange={option => setForm({ ...form, managerId: option.value })}
              />
              <Select
                size="small"
                label={intl.formatMessage(m.overtimeExemption)}
                options={exemptionOptions}
                value={
                  exemptionOptions.find(o => o.value === (form.overtimeExemption ?? "")) ?? null
                }
                onChange={option => setForm({ ...form, overtimeExemption: option.value })}
              />
              {form.overtimeExemption && (
                <Input
                  size="small"
                  type="date"
                  label={intl.formatMessage(m.exemptionUntil)}
                  value={String(form.overtimeExemptionUntil ?? "")}
                  onChange={e => setForm({ ...form, overtimeExemptionUntil: e.target.value })}
                />
              )}
              <Checkbox
                checked={form.teleworkAllowed === true}
                onCheckedChange={checked => setForm({ ...form, teleworkAllowed: checked === true })}
              >
                <Text size={2}>{intl.formatMessage(l.telework)}</Text>
              </Checkbox>
            </Box>
            {form.scheduleType !== ScheduleTypeEnum.EXEMPT && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <Text size={3} fontWeight="bold">
                    {intl.formatMessage(l.schedule)}
                    {followsDefault && ` (${intl.formatMessage(m.companyHours)})`}
                  </Text>
                  {!followsDefault && (
                    <Button
                      size="small"
                      variant="tertiary"
                      onClick={() => {
                        setFollowsDefault(true);
                        setSchedule(weekFromRows(data?.timekeepingRules?.defaultSchedule ?? []));
                      }}
                    >
                      {intl.formatMessage(m.useCompanyHours)}
                    </Button>
                  )}
                </Box>
                <WeekScheduleEditor
                  week={schedule}
                  onChange={week => {
                    setFollowsDefault(false);
                    setSchedule(week);
                  }}
                />
              </Box>
            )}
            <ErrorText errors={saveOpts.data?.employeeProfileSave?.errors} />
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setProfileEditing(false);
                  resetForm();
                }}
              >
                {intl.formatMessage(m.cancel)}
              </Button>
              <Button variant="primary" disabled={saveOpts.loading} onClick={handleSave}>
                {intl.formatMessage(m.save)}
              </Button>
            </Box>
          </>
        )}
      </Section>
    </Page>
  );
};
